import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PlanCard from "@/components/PlanCard";
import { Link } from "react-router-dom";
import { ShieldCheck, Info, Lock } from "@phosphor-icons/react";
import { logWarn } from "@/lib/logger";

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [accepted, setAccepted] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plans");
        setPlans(data.plans || []);
      } catch (e) {
        logWarn("Failed to load plans:", e?.message || e);
        // Fallback in prod: silently degrade — user sees empty grid rather than a broken page.
      }
    })();
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16" data-testid="plans-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="label-eyebrow text-brand-terracotta">Membership</div>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tighter mt-4 text-brand-ink">
          Choose the level of<br /><em className="text-brand-terracotta">personal service</em>
        </h1>
        <p className="mt-6 text-brand-muted leading-relaxed">
          Each plan is a <strong className="text-brand-ink">one-time payment</strong> for a fixed duration.
          <br />No auto-renewal. No hidden charges.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-brand-line border border-brand-line" data-testid="plans-grid">
        {plans.map((p) => (
          <PlanCard
            key={p.id}
            plan={p}
            featured={p.id === "golden"}
            isCurrent={user?.plan === p.id}
            paymentDisabled={!accepted && p.id !== "free"}
          />
        ))}
      </div>

      {/* Compliance & Consent block */}
      <div className="mt-10 bg-white border border-brand-line p-8" data-testid="compliance-consent">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} weight="duotone" className="text-brand-terracotta" />
          <div className="label-eyebrow text-brand-terracotta">Terms &amp; Compliance · Please read before subscribing</div>
        </div>
        <ul className="mt-4 text-sm text-brand-ink space-y-2 leading-relaxed">
          <li>· <strong>Matrimonial introduction service:</strong> WR Rishtey facilitates introductions between prospective families. We do not guarantee marriage, engagement or any specific outcome.</li>
          <li>· <strong>One-time payment · no auto-renewal:</strong> Your membership auto-expires at the end of the plan duration. You choose when (or whether) to renew.</li>
          <li>· <strong>Refunds:</strong> Governed by our <Link to="/refund" className="text-brand-terracotta underline hover:no-underline">Refund Policy</Link>. Requests within 7 days may be considered if the plan was purchased in error.</li>
          <li>· <strong>Data privacy:</strong> We collect and process your information per our <Link to="/privacy" className="text-brand-terracotta underline hover:no-underline">Privacy Policy</Link>. Your government identity documents are stored privately and never shown to other members.</li>
          <li>· <strong>Safe conduct:</strong> All members abide by our <Link to="/safe-match" className="text-brand-terracotta underline hover:no-underline">SafeMatch Pledge</Link>. Verify information about proposed matches independently; be cautious sharing personal or financial details.</li>
          <li>· <strong>Payments:</strong> Processed by Razorpay (PCI-DSS compliant). WR Rishtey does not store card, UPI or bank credentials.</li>
          <li>· <strong>Age &amp; consent:</strong> You confirm you are of legal marriageable age in your jurisdiction and that the profile is created with the subject&rsquo;s consent.</li>
        </ul>
        <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="tnc-checkbox-label">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            data-testid="tnc-checkbox"
            className="mt-1 w-4 h-4 accent-brand-terracotta"
          />
          <span className="text-sm text-brand-ink">
            I have read and agree to the <Link to="/terms" className="text-brand-terracotta underline">Terms of Use</Link>,
            <Link to="/privacy" className="text-brand-terracotta underline ml-1">Privacy Policy</Link>,
            <Link to="/refund" className="text-brand-terracotta underline ml-1">Refund Policy</Link> and
            <Link to="/safe-match" className="text-brand-terracotta underline ml-1">SafeMatch Pledge</Link>.
            I understand this is a one-time membership fee for the selected duration with no auto-renewal.
          </span>
        </label>
        {!accepted && (
          <p className="mt-3 text-xs text-brand-muted flex items-center gap-1"><Info size={12} /> Please accept the terms above to enable payment on paid plans.</p>
        )}
      </div>

      {/* Payment security strip */}
      <div className="mt-8 flex flex-col md:flex-row items-center justify-center gap-4 text-xs text-brand-muted">
        <span className="inline-flex items-center gap-1"><Lock size={14} weight="duotone" className="text-brand-terracotta" /> 256-bit TLS encryption</span>
        <span className="hidden md:inline">·</span>
        <span>Powered by Razorpay · PCI-DSS SAQ-A compliant</span>
        <span className="hidden md:inline">·</span>
        <span>Manual UPI claims verified within 24 hours</span>
      </div>

      <p className="text-center text-[11px] text-brand-muted mt-8 max-w-3xl mx-auto">
        Prices are inclusive of applicable taxes (GST) where required. WR Rishtey is a service mark of WR Rishtey Enterprise, India. All disputes are subject to the exclusive jurisdiction of the courts at Mumbai, India.
      </p>
    </div>
  );
}
